import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Book } from 'lucide-react';
import { api, Account } from '../../lib/api';

export function LedgerPage() {
  const [selectedAccount, setSelectedAccount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ data: Account[] }>('/api/accounts'),
  });

  const { data: ledger, isLoading } = useQuery({
    queryKey: ['ledger', selectedAccount, startDate, endDate],
    queryFn: () => api.get<{ data: any[] }>(`/api/journals/ledger/${selectedAccount}`, { startDate, endDate }),
    enabled: !!selectedAccount,
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
        総勘定元帳
      </h1>

      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm min-w-[200px]"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="">勘定科目を選択...</option>
            {accounts?.data.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.code} {acc.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>〜</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {!selectedAccount ? (
          <div className="text-center py-12">
            <Book size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>勘定科目を選択してください</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ color: 'var(--text-primary)' }}>日付</th>
                <th style={{ color: 'var(--text-primary)' }}>仕訳番号</th>
                <th style={{ color: 'var(--text-primary)' }}>摘要</th>
                <th className="text-right" style={{ color: 'var(--text-primary)' }}>借方</th>
                <th className="text-right" style={{ color: 'var(--text-primary)' }}>貸方</th>
                <th className="text-right" style={{ color: 'var(--text-primary)' }}>残高</th>
              </tr>
            </thead>
            <tbody>
              {ledger?.data.map((entry: any) => (
                <tr key={entry.id}>
                  <td style={{ color: 'var(--text-primary)' }}>{entry.journalDate}</td>
                  <td className="font-mono" style={{ color: 'var(--text-secondary)' }}>{entry.journalNumber}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{entry.description || entry.journalDescription || '-'}</td>
                  <td className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                    {parseFloat(entry.debitAmount) > 0 ? `¥${parseFloat(entry.debitAmount).toLocaleString()}` : '-'}
                  </td>
                  <td className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                    {parseFloat(entry.creditAmount) > 0 ? `¥${parseFloat(entry.creditAmount).toLocaleString()}` : '-'}
                  </td>
                  <td className="text-right font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                    ¥{entry.balance.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

